import React, { useCallback } from "react";
import { useNavigate } from "react-router-dom";

import UserContextValueProvider from "../../Providers/Value/UserContextValueProvider";
import LoginProcess, { ILoginProcessProps, ILoginStepProps } from "../Components/LoginProcess";
import UserContextIvrAuthenticationLoginStep from "./IvrAuthenticationLoginStep";
import UserContextDependenciesLoadingCard from "./UserContextDependenciesLoadingCard";

export interface ILoginPageRouteProps {
  /** The form component which should be rendered to the user for their input to obtain a security token for the
   * session.
   */
  loginForm: React.FC<ILoginStepProps>;
}

type IUserContextLoginPageProps = Pick<ILoginProcessProps, "setLoginLeadId"> & ILoginPageRouteProps;

/** Login steps which are used universally regardless of the authentication form used. These load application
 * dependencies.
 */
const universalLoginProcessSteps: ILoginProcessProps["steps"] = [
  { Component: UserContextIvrAuthenticationLoginStep, showAnimation: true },
  { Component: UserContextDependenciesLoadingCard, showAnimation: true }
];

/** A wrapping of the login process with the action to navigate to the root application route when it indicates
 * completion.
 */
const LoginPageWithCompleteAction: React.FC<IUserContextLoginPageProps> = ({ loginForm, ...rest }) => {
  const navigate = useNavigate();
  const onComplete = useCallback(() => navigate("/"), []);

  return LoginProcess({
    ...rest,
    windowSearchParams: window.location.search,
    onComplete,
    steps: [{ Component: loginForm, showAnimation: true }, ...universalLoginProcessSteps]
  });
};

/** A login view which allows for the capture of a login-lead and uses a specified login form. */
const UserContextLoginExperience: React.FC<ILoginPageRouteProps> = UserContextValueProvider<
  Pick<ILoginProcessProps, "setLoginLeadId">,
  IUserContextLoginPageProps
>({
  Component: LoginPageWithCompleteAction,
  map: ({ setLoginLeadId }) => ({ setLoginLeadId })
});

export default UserContextLoginExperience;
